import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  Building, 
  Save,
  Upload,
  X,
  AlertCircle,
  Check,
  Sparkles,
  ChevronDown
} from 'lucide-react';

import { Toast } from "../components/ui/Toast";
import api from '../services/api'; 
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useParamsCompta } from '../contexts/ParametresComptaContext';

const NewEmployeeFormSimple = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastInfo, setToastInfo] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [generatingBadge, setGeneratingBadge] = useState(false);

  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const deptWrapperRef = useRef(null);
  const [salaryTypeDropdownOpen, setSalaryTypeDropdownOpen] = useState(false);
  const salaryTypeWrapperRef = useRef(null);
  const groupeWrapperRef = useRef(null);
  const [groupeDropdownOpen, setGroupeDropdownOpen] = useState(false);

  // État pour la capture RFID temporaire
  const [rfidCapture, setRfidCapture] = useState('');
  // État pour savoir si le champ RFID est actif
  const [rfidFieldActive, setRfidFieldActive] = useState(false);
  
  // États pour les listes de référence
  const [departments, setDepartments] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [contractTypeDropdownOpen, setContractTypeDropdownOpen] = useState(false);
  const contractTypeWrapperRef = useRef(null);

  // ETAT POUR LES GROUPES SALARIAUX 
  const [groupesSalariaux, setGroupesSalariaux] = useState([]);
  const { symbole } = useParamsCompta();

  // Feature flag 
  const { isEnabled: rfidEnabled } = useFeatureFlag('rfid_attendance');
  const { isEnabled: contratsEnabled } = useFeatureFlag('contrats_employes'); 
  
  // État principal du formulaire
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    hire_date: '',
    badge_number: '',
    rfid_uid: '', 
    position: '',
    department: '',
    salary: '',
    mode_paiement_salaire: 'especes',
    salary_type: 'monthly',
    contract_start_date: '',
    contract_end_date: '',
    trial_period_end_date: '',
    status: 'active',
    date_of_birth: '',
    gender: 'M',
    address: '',
    city: '',
    groupe_salarial_id: '',
    taux_horaire_personnalise: '',
    photo: null,
    contract_type_id: null
    
  });


  // Charger les départements
useEffect(() => {
  const fetchDepartments = async () => {
    try {
      const response = await api.departements.getAll();
      const deptData = response.data || response;
      setDepartments(Array.isArray(deptData) ? deptData : []);
    } catch (err) {
      console.error('❌ Erreur chargement départements:', err);
      setError('Erreur lors du chargement des départements');
    }
  };
  fetchDepartments();

  if (contratsEnabled) {
    const fetchContractTypes = async () => {
      try {
        const response = await api.contractTypes.getAll();
        if (response.success) setContractTypes(response.data);
      } catch (err) {
        console.error('Erreur chargement types contrats:', err);
      }
    };
    fetchContractTypes();
  }

  const fetchGroupes = async () => {
    try {
      const r = await api.groupesSalariaux.getAll();
      if (r.success) setGroupesSalariaux(r.data);
    } catch (e) { console.error('Erreur chargement groupes:', e); }
  };
  fetchGroupes();

}, [contratsEnabled]);



 //useEffect de fermeture des dropdowns

useEffect(() => {
    const handleClickOutside = (e) => {
      if (deptWrapperRef.current && !deptWrapperRef.current.contains(e.target)) {
        setDeptDropdownOpen(false);
      }
      if (salaryTypeWrapperRef.current && !salaryTypeWrapperRef.current.contains(e.target)) {
        setSalaryTypeDropdownOpen(false);
      }
      if (contractTypeWrapperRef.current && !contractTypeWrapperRef.current.contains(e.target)) {
        setContractTypeDropdownOpen(false);
      }
      if (groupeWrapperRef.current && !groupeWrapperRef.current.contains(e.target)) {
        setGroupeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleFileChange = (e) => {
    // 🔒 IMPORTANT : Empêcher la propagation
    e.stopPropagation();
    
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setError("La taille de l'image ne doit pas dépasser 5MB");
      return;
    }
    
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      setError("Seuls les formats JPG et PNG sont acceptés");
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
      setFormData(prev => ({ ...prev, photo: file }));
    };
    reader.readAsDataURL(file);
  };

  const goToNextStep = () => {
    console.log('⏭️ Passage à l\'étape suivante');
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
      setError('');
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousStep = () => {
    console.log('⏮️ Retour à l\'étape précédente');
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError('');
      window.scrollTo(0, 0);
    }
  };

  // ============================================================================
  // GÉNÉRATION BADGE AUTOMATIQUE
  // ============================================================================
  
  const handleGenerateBadge = async (e) => {
    // 🔒 EMPÊCHER SUBMIT
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      setGeneratingBadge(true);
      setError('');
      
      console.log('🎫 Génération badge...');
      
      const response = await api.employees.generateBadge();
      
      if (response.success && response.badge) {
        console.log('✅ Badge généré:', response.badge);
        
        setFormData(prev => ({ ...prev, badge_number: response.badge }));
        
        setToastInfo({
          message: `Badge généré : ${response.badge}`,
          type: 'success'
        });
      }
      
    } catch (err) {
      console.error('❌ Erreur génération badge:', err);
      setError('Erreur lors de la génération du badge');
      setToastInfo({
        message: 'Erreur lors de la génération du badge',
        type: 'error'
      });
    } finally {
      setGeneratingBadge(false);
    }
  };

  // ============================================================================
  // VALIDATION
  // ============================================================================
  
  const validateForm = () => {
    if (!formData.first_name?.trim()) {
      setError('Le prénom est obligatoire');
      setCurrentStep(1);
      return false;
    }
    
    if (!formData.last_name?.trim()) {
      setError('Le nom est obligatoire');
      setCurrentStep(1);
      return false;
    }
    
    if (!formData.email?.trim()) {
      setError('L\'email est obligatoire');
      setCurrentStep(1);
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Format d\'email invalide');
      setCurrentStep(1);
      return false;
    }
    
    if (!formData.phone?.trim()) {
      setError('Le téléphone est obligatoire');
      setCurrentStep(1);
      return false;
    }
    
    if (!formData.hire_date) {
      setError('La date d\'embauche est obligatoire');
      setCurrentStep(2);
      return false;
    }

    // Validation dates contrat vs période d'essai
    if (formData.trial_period_end_date && formData.contract_start_date) {
      if (new Date(formData.contract_start_date) <= new Date(formData.trial_period_end_date)) {
        setError('La date de début du contrat doit être après la fin de la période d\'essai');
        setCurrentStep(2);
        return false;
      }
    }

    if (formData.trial_period_end_date && formData.contract_end_date) {
      if (new Date(formData.contract_end_date) <= new Date(formData.trial_period_end_date)) {
        setError('La date de fin du contrat doit être après la fin de la période d\'essai');
        setCurrentStep(2);
        return false;
      }
    }
    
    return true;
  };

  // ============================================================================
  // 🔒 SOUMISSION SÉCURISÉE
  // ============================================================================

  const handleSubmit = async (e) => {
    // 🔒 TOUJOURS empêcher le comportement par défaut
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🚨 handleSubmit appelé - Étape actuelle:', currentStep);
    console.log('🚨 Loading état:', loading);
    
    // 🔒 VÉRIFICATION 1 : Bloquer si déjà en cours
    if (loading) {
      console.log('⚠️ Déjà en cours de traitement, ABANDON');
      return;
    }
    
    // 🔒 VÉRIFICATION 2 : Bloquer si pas à l'étape 3
    if (currentStep !== 3) {
      console.log('⚠️ Pas à l\'étape 3 (actuelle: ' + currentStep + '), passage à l\'étape suivante');
      goToNextStep();
      return;
    }
    
    console.log('✅ Validation OK, création employé...');
    
    // 🔒 VÉRIFICATION 3 : Validation complète
    if (!validateForm()) {
      console.log('❌ Validation échouée');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      console.log('📤 Envoi données employé...');
      
      const employeePayload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        hire_date: formData.hire_date,
        badge_number: formData.badge_number?.trim() || null,
        rfid_uid: formData.rfid_uid?.trim() || null,  
        position: formData.position?.trim() || null,
        department: formData.department || null,
        contract_type_id: formData.contract_type_id || null,
        contract_start_date: formData.contract_start_date || null,
        contract_end_date: formData.contract_end_date || null,
        trial_period_end_date: formData.trial_period_end_date || null,
        salary: formData.salary ? parseFloat(formData.salary) : null,
        mode_paiement_salaire: formData.mode_paiement_salaire || 'especes',
        salary_type: formData.salary_type,
        status: formData.status,
        date_of_birth: formData.date_of_birth || null,
        gender: formData.gender,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        groupe_salarial_id: formData.groupe_salarial_id || null,
        taux_horaire_personnalise: formData.taux_horaire_personnalise || null,
      };
      
      const createResponse = await api.employees.create(employeePayload);
      const employeeId = createResponse.id;
      
      console.log('✅ Employé créé avec ID:', employeeId);
      
      // Upload photo si présente
      if (formData.photo && employeeId) {
        console.log('📸 Upload photo...');
        const photoFormData = new FormData();
        photoFormData.append('photo', formData.photo);
        await api.employees.uploadPhoto(employeeId, photoFormData);
        console.log('✅ Photo uploadée');
      }
      
      setToastInfo({
        message: 'Employé créé avec succès !',
        type: 'success'
      });
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
        resetForm();
      }, 1500);
      
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message || 'Erreur lors de la création de l\'employé');
      setToastInfo({
        message: err.message || "Une erreur est survenue",
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      hire_date: '',
      badge_number: '',
      rfid_uid: '',
      position: '',
      department: '',
      contract_type_id: null,
      salary: '',
      salary_type: 'monthly',
      status: 'active',
      date_of_birth: '',
      gender: 'M',
      address: '',
      city: '',
      photo: null
    });
    setPreviewImage(null);
    setCurrentStep(1);
    setError('');
    setLoading(false);
  };

  const renderError = () => {
    if (!error) return null;
    return (
      <div className="p-4 rounded-xl border-2 flex items-start gap-3 mb-6 animate-slideDown" style={{ backgroundColor: '#fff5e5', borderColor: '#fed774' }}>
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fed774' }}>
          <AlertCircle size={18} className="text-gray-900" />
        </div>
        <div>
          <p className="font-semibold text-gray-900">Attention</p>
          <p className="text-sm text-gray-700">{error}</p>
        </div>
      </div>
    );
  };


// ============================================================================
// GESTION CAPTURE RFID
// ============================================================================

const handleRfidKeyDown = (e) => {
  // Ignorer les touches de modification
  if (e.keyCode === 16 || e.keyCode === 17 || e.keyCode === 18) return;
  
  // Si c'est Enter avec un badge, enregistrer
  if (e.keyCode === 13 && rfidCapture.trim()) {
    e.preventDefault();
    setFormData(prev => ({ ...prev, rfid_uid: rfidCapture.trim() }));
    setRfidCapture('');
    
    // Toast de confirmation
    setToastInfo({
      message: `Badge RFID capturé : ${rfidCapture.trim()}`,
      type: 'success'
    });
    
    return;
  }
  
  // Ignorer tout ce qui n'est pas alphanumérique
  if (!((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 65 && e.keyCode <= 90))) {
    e.preventDefault();
    return;
  }
  
  // Ajouter le caractère
  const char = String.fromCharCode(e.keyCode);
  setRfidCapture(prev => prev + char);
  
  // ✅ Auto-reset après 500ms d'inactivité (pour ignorer saisie manuelle)
  clearTimeout(window.rfidFormTimeout);
  window.rfidFormTimeout = setTimeout(() => {
    setRfidCapture('');
  }, 500);
  
  e.preventDefault();
};


  // ============================================================================
  // ÉTAPE 1
  // ============================================================================
  const renderStep1 = () => (
    <div className="space-y-6">
      {renderError()}
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">

       <div className="px-6 py-4 flex items-center gap-3 rounded-t-2xl" style={{ backgroundColor: '#448696' }}>
          <User size={24} className="text-white" />
          <h3 className="font-bold text-white text-lg">Informations personnelles</h3>
        </div>
        
       <div className="p-6 overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Prénom */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Prénom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                onFocus={(e) => {
                  e.target.style.borderColor = '#448696';
                  e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e5e5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            {/* Nom */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Nom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                onFocus={(e) => {
                  e.target.style.borderColor = '#448696';
                  e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e5e5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            {/* Sexe */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Sexe <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, gender: 'M' }))}
                  className={`flex-1 px-4 py-3 rounded-xl transition-all duration-200 font-semibold text-sm ${
                    formData.gender === 'M' ? 'text-white shadow-md' : 'text-gray-700 bg-white border border-gray-200'
                  }`}
                  style={{
                    backgroundColor: formData.gender === 'M' ? '#448696' : undefined
                  }}
                >
                  Masculin
                </button>
                
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, gender: 'F' }))}
                  className={`flex-1 px-4 py-3 rounded-xl transition-all duration-200 font-semibold text-sm ${
                    formData.gender === 'F' ? 'text-white shadow-md' : 'text-gray-700 bg-white border border-gray-200'
                  }`}
                  style={{
                    backgroundColor: formData.gender === 'F' ? '#448696' : undefined
                  }}
                >
                  Féminin
                </button>
              </div>
            </div>
            
            {/* Date de naissance */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Date de naissance
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                onFocus={(e) => {
                  e.target.style.borderColor = '#448696';
                  e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e5e5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            {/* Email */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                onFocus={(e) => {
                  e.target.style.borderColor = '#448696';
                  e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e5e5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            {/* Téléphone */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Téléphone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                onFocus={(e) => {
                  e.target.style.borderColor = '#448696';
                  e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e5e5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            {/* Adresse */}
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Adresse
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                onFocus={(e) => {
                  e.target.style.borderColor = '#448696';
                  e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e5e5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            {/* Ville */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Ville
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                onFocus={(e) => {
                  e.target.style.borderColor = '#448696';
                  e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e5e5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // ÉTAPE 2
  // ============================================================================
const renderStep2 = () => (
    <div className="space-y-6">
      {renderError()}
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">

       <div className="px-6 py-4 flex items-center gap-3 rounded-t-2xl" style={{ backgroundColor: '#448696' }}>
          <Building size={24} className="text-white" />
          <h3 className="font-bold text-white text-lg">Informations professionnelles</h3>
        </div>
        
        <div className="p-6 space-y-6">

          {/* ── BLOC 1 : Identification ── */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Identification</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Date d'embauche */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">
                  Date d'embauche <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="hire_date"
                  value={formData.hire_date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                  onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              {/* Numéro de badge */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">
                  Numéro de badge
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="badge_number"
                    value={formData.badge_number}
                    onChange={handleInputChange}
                    className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                    onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
                    placeholder="DA001"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateBadge}
                    disabled={generatingBadge}
                    className="px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#fed774', color: '#1f2937' }}
                    onMouseEnter={(e) => { if (!generatingBadge) e.currentTarget.style.backgroundColor = '#fdd24e'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fed774'; }}
                    title="Générer un badge automatique"
                  >
                    {generatingBadge ? (
                      <>
                        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin border-gray-900"></div>
                        <span className="hidden sm:inline">Génération...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} />
                        <span className="hidden sm:inline">Générer</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Saisissez un numéro de badge ou cliquez sur "Générer"
                </p>
              </div>

              {/* Badge RFID */}
              {rfidEnabled && (
                <div className="sm:col-span-2">
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    Numéro de badge RFID (scan automatique)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value=""
                      onChange={() => {}}
                      onKeyDown={handleRfidKeyDown}
                      onFocus={(e) => {
                        setRfidFieldActive(true);
                        e.target.style.borderColor = '#448696';
                        e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)';
                      }}
                      onBlur={(e) => {
                        setRfidFieldActive(false);
                        e.target.style.borderColor = '#e5e5e5';
                        e.target.style.boxShadow = 'none';
                      }}
                      className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
                      placeholder="Cliquez ici et scannez le badge RFID"
                    />
                    <div
                      className="w-full px-4 py-3 border-2 rounded-xl font-bold text-base transition-all duration-200"
                      style={{ 
                        backgroundColor: rfidFieldActive ? '#f0f9fb' : (formData.rfid_uid ? '#f0f9fb' : '#f9fafb'),
                        borderColor: rfidFieldActive ? '#448696' : (formData.rfid_uid ? '#448696' : '#e5e7eb'),
                        color: formData.rfid_uid ? '#1f2937' : '#9ca3af',
                        cursor: 'pointer',
                        boxShadow: rfidFieldActive ? '0 0 0 3px rgba(136, 185, 197, 0.1)' : 'none'
                      }}
                      onClick={() => {
                        const hiddenInput = document.querySelector('input[placeholder="Cliquez ici et scannez le badge RFID"]');
                        if (hiddenInput) hiddenInput.focus();
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span>{formData.rfid_uid || 'En attente du scan RFID...'}</span>
                        {rfidFieldActive && !formData.rfid_uid && (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-[#448696] animate-pulse"></div>
                            <span className="text-xs font-semibold text-[#448696]">Prêt à scanner</span>
                          </div>
                        )}
                        {rfidCapture && (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                            <span className="text-xs font-semibold text-green-600">Lecture en cours...</span>
                          </div>
                        )}
                        {formData.rfid_uid && !rfidFieldActive && (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-xs font-semibold text-green-600">✓ Capturé</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {rfidFieldActive ? 'Champ actif - Approchez votre badge du lecteur' : 'Cliquez sur le champ et approchez votre badge RFID du lecteur'}
                  </p>
                  {formData.rfid_uid && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, rfid_uid: '' }));
                        setToastInfo({ message: 'Badge RFID effacé', type: 'info' });
                      }}
                      className="mt-2 text-xs font-semibold hover:underline"
                      style={{ color: '#448696' }}
                    >
                      Effacer le badge RFID
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Séparateur */}
          <div className="border-t border-gray-100" />

          {/* ── BLOC 2 : Poste ── */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Poste</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Poste */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Poste</label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                  onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
                  placeholder="Technicien"
                />
              </div>

              {/* Département */}
              <div ref={deptWrapperRef} className="relative">
                <label className="text-sm font-semibold text-gray-700 block mb-2">Département</label>
                <button
                  type="button"
                  onClick={() => setDeptDropdownOpen(prev => !prev)}
                  className="w-full px-4 py-3 bg-white border-2 rounded-xl font-medium text-sm transition-all flex items-center justify-between"
                  style={{
                    borderColor: deptDropdownOpen ? '#448696' : '#e5e7eb',
                    boxShadow: deptDropdownOpen ? '0 0 0 3px rgba(136, 185, 197, 0.1)' : 'none'
                  }}
                >
                  <span style={{ color: formData.department ? '#1f2937' : '#9ca3af' }}>
                    {formData.department || 'Sélectionnez un département'}
                  </span>
                  <ChevronDown size={16} className="text-gray-400 transition-transform"
                    style={{ transform: deptDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                </button>
                {deptDropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-56 overflow-y-auto">
                    <button type="button" onClick={() => { setFormData(prev => ({ ...prev, department: '' })); setDeptDropdownOpen(false); }}
                      className="w-full flex items-center px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50 text-sm text-gray-400 font-medium">
                      — Aucun département —
                    </button>
                    {departments.map(dept => (
                      <button type="button" key={dept.id}
                        onClick={() => { setFormData(prev => ({ ...prev, department: dept.name })); setDeptDropdownOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50 last:border-0"
                        style={{ backgroundColor: formData.department === dept.name ? '#f0f9fb' : undefined }}>
                        <div className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: formData.department === dept.name ? '#448696' : '#d1d5db' }} />
                        <span className="text-sm font-medium"
                          style={{ color: formData.department === dept.name ? '#448696' : '#374151' }}>
                          {dept.name}
                        </span>
                        {formData.department === dept.name && (
                          <Check size={14} className="ml-auto" style={{ color: '#448696' }} />
                        )}
                      </button>
                    ))}
                    {departments.length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-400 text-center">Aucun département disponible</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Séparateur */}
         {contratsEnabled && <div className="border-t border-gray-100" />}



          {/* ── BLOC 3 : Contrat ── */}
          {contratsEnabled && (
           <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Contrat</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Type de contrat */}
              <div ref={contractTypeWrapperRef} className="relative">
                <label className="text-sm font-semibold text-gray-700 block mb-2">Type de contrat</label>
                <button
                  type="button"
                  onClick={() => setContractTypeDropdownOpen(prev => !prev)}
                  className="w-full px-4 py-3 bg-white border-2 rounded-xl font-medium text-sm transition-all flex items-center justify-between"
                  style={{
                    borderColor: contractTypeDropdownOpen ? '#448696' : '#e5e7eb',
                    boxShadow: contractTypeDropdownOpen ? '0 0 0 3px rgba(136, 185, 197, 0.1)' : 'none'
                  }}
                >
                  <span style={{ color: formData.contract_type_id ? '#1f2937' : '#9ca3af' }}>
                    {contractTypes.find(ct => ct.id === formData.contract_type_id)?.nom || 'Sélectionnez un type de contrat'}
                  </span>
                  <ChevronDown size={16} className="text-gray-400 transition-transform"
                    style={{ transform: contractTypeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                </button>
                {contractTypeDropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-56 overflow-y-auto">
                    <button type="button"
                      onClick={() => { setFormData(prev => ({ ...prev, contract_type_id: null })); setContractTypeDropdownOpen(false); }}
                      className="w-full flex items-center px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50 text-sm text-gray-400 font-medium">
                      — Aucun type —
                    </button>
                    {contractTypes.map(ct => (
                      <button type="button" key={ct.id}
                        onClick={() => { setFormData(prev => ({ ...prev, contract_type_id: ct.id })); setContractTypeDropdownOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50 last:border-0"
                        style={{ backgroundColor: formData.contract_type_id === ct.id ? '#f0f9fb' : undefined }}>
                        <div className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: formData.contract_type_id === ct.id ? '#448696' : '#d1d5db' }} />
                        <span className="text-sm font-medium"
                          style={{ color: formData.contract_type_id === ct.id ? '#448696' : '#374151' }}>
                          {ct.nom}
                        </span>
                        {formData.contract_type_id === ct.id && (
                          <Check size={14} className="ml-auto" style={{ color: '#448696' }} />
                        )}
                      </button>
                    ))}
                    {contractTypes.length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-400 text-center">Aucun type de contrat configuré</div>
                    )}
                  </div>
                )}
              </div>

              {/* Date début contrat */}
            {/* Date début contrat */}
                  {formData.contract_type_id && (
                    <div>
                      <label className="text-sm font-semibold text-gray-700 block mb-2">
                        {formData.trial_period_end_date ? 'Date de début prévisionnelle du contrat' : 'Date de début du contrat'}
                        {formData.trial_period_end_date && (
                          <span className="ml-2 text-xs font-normal text-amber-600">
                          Doit être après la fin de la période d'essai
                          </span>
                        )}
                      </label>

                  <input
                    type="date"
                    name="contract_start_date"
                    value={formData.contract_start_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                    onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              )}

              {/* Date fin contrat — uniquement si durée déterminée */}

             {formData.contract_type_id && contractTypes.find(ct => ct.id === formData.contract_type_id)?.has_end_date == 1 && (
                    <div>
                      <label className="text-sm font-semibold text-gray-700 block mb-2">
                        {formData.trial_period_end_date ? 'Date de fin prévisionnelle du contrat' : 'Date de fin du contrat'}
                        <span className="text-red-500 ml-1">*</span>
                        {formData.trial_period_end_date && (
                          <span className="ml-2 text-xs font-normal text-amber-600">
                           Doit être après la fin de la période d'essai
                          </span>
                        )}
                      </label>
                  <input
                    type="date"
                    name="contract_end_date"
                    value={formData.contract_end_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                    onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Contrat à durée déterminée - date de fin obligatoire</p>
                </div>
              )}

              {/* Période d'essai */}
              {formData.contract_type_id && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    Fin de période d'essai <span className="text-gray-400 font-normal">(optionnel)</span>
                  </label>
                  <input
                    type="date"
                    name="trial_period_end_date"
                    value={formData.trial_period_end_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                    onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              )}
            </div>
          </div>
          )}


          {/* Séparateur */}
          <div className="border-t border-gray-100" />

         
          {/* ── BLOC 4 : Rémunération ── */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Rémunération</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

              {/* Type de rémunération */}
              <div ref={salaryTypeWrapperRef} className="relative">
                <label className="text-sm font-semibold text-gray-700 block mb-2">Type de rémunération</label>
                <button
                  type="button"
                  onClick={() => setSalaryTypeDropdownOpen(prev => !prev)}
                  className="w-full px-4 py-3 bg-white border-2 rounded-xl font-medium text-sm transition-all flex items-center justify-between"
                  style={{
                    borderColor: salaryTypeDropdownOpen ? '#448696' : '#e5e7eb',
                    boxShadow: salaryTypeDropdownOpen ? '0 0 0 3px rgba(136, 185, 197, 0.1)' : 'none'
                  }}
                >
                  <span style={{ color: '#1f2937' }}>
                    {formData.salary_type === 'monthly' ? 'Mensuel (salaire fixe)' : 'Horaire (taux horaire)'}
                  </span>
                  <ChevronDown size={16} className="text-gray-400 transition-transform"
                    style={{ transform: salaryTypeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                </button>
                {salaryTypeDropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-50">
                    {[
                      { value: 'monthly', label: 'Mensuel (salaire fixe)' },
                      { value: 'hourly',  label: 'Horaire (taux horaire)' }
                    ].map(({ value, label }) => (
                      <button type="button" key={value}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, salary_type: value, salary: '', groupe_salarial_id: '', taux_horaire_personnalise: '' }));
                          setSalaryTypeDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50 last:border-0"
                        style={{ backgroundColor: formData.salary_type === value ? '#f0f9fb' : undefined }}>
                        <div className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: formData.salary_type === value ? '#448696' : '#d1d5db' }} />
                        <span className="text-sm font-medium"
                          style={{ color: formData.salary_type === value ? '#448696' : '#374151' }}>
                          {label}
                        </span>
                        {formData.salary_type === value && (
                          <Check size={14} className="ml-auto" style={{ color: '#448696' }} />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Salaire fixe — visible si monthly */}
              {formData.salary_type === 'monthly' && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    Salaire mensuel {symbole && `(${symbole})`}
                  </label>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                    onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
                    placeholder="500000"
                  />
                </div>
              )}

              {/* Groupe salarial — visible si hourly */}
              {/* Groupe salarial — visible si hourly */}
              {formData.salary_type === 'hourly' && (
                <div ref={groupeWrapperRef} className="relative">
                  <label className="text-sm font-semibold text-gray-700 block mb-2">Groupe salarial</label>
                  <button
                    type="button"
                    onClick={() => setGroupeDropdownOpen(prev => !prev)}
                    className="w-full px-4 py-3 bg-white border-2 rounded-xl font-medium text-sm transition-all flex items-center justify-between"
                    style={{
                      borderColor: groupeDropdownOpen ? '#448696' : '#e5e7eb',
                      boxShadow: groupeDropdownOpen ? '0 0 0 3px rgba(136, 185, 197, 0.1)' : 'none'
                    }}
                  >
                    <span style={{ color: formData.groupe_salarial_id ? '#1f2937' : '#9ca3af' }}>
                      {formData.groupe_salarial_id
                        ? groupesSalariaux.find(g => g.id == formData.groupe_salarial_id)?.nom || 'Groupe sélectionné'
                        : '-- Sélectionner un groupe --'}
                    </span>
                    <ChevronDown size={16} className="text-gray-400 transition-transform"
                      style={{ transform: groupeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                  </button>

                  {groupeDropdownOpen && (
                    <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-50">
                      <button type="button"
                        onClick={() => { setFormData(p => ({ ...p, groupe_salarial_id: '' })); setGroupeDropdownOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50">
                        <div className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-300" />
                        <span className="text-sm font-medium text-gray-400">-- Aucun groupe --</span>
                      </button>
                      {groupesSalariaux.map(g => (
                        <button type="button" key={g.id}
                          onClick={() => { setFormData(p => ({ ...p, groupe_salarial_id: g.id })); setGroupeDropdownOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left border-b border-gray-50 last:border-0"
                          style={{ backgroundColor: formData.groupe_salarial_id == g.id ? '#f0f9fb' : undefined }}>
                          <div className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: formData.groupe_salarial_id == g.id ? '#448696' : '#d1d5db' }} />
                          <div className="flex-1">
                            <span className="text-sm font-medium block"
                              style={{ color: formData.groupe_salarial_id == g.id ? '#448696' : '#374151' }}>
                              {g.nom}
                            </span>
                            <span className="text-xs text-gray-400">
                              {parseFloat(g.taux_horaire).toLocaleString('fr-FR')} {symbole}/h · {g.heures_journalieres}h/jour
                            </span>
                          </div>
                          {formData.groupe_salarial_id == g.id && (
                            <Check size={14} className="ml-auto" style={{ color: '#448696' }} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {formData.groupe_salarial_id && groupesSalariaux.find(g => g.id == formData.groupe_salarial_id) && (
                    <p className="text-xs text-gray-400 mt-1">
                      Taux groupe : {parseFloat(groupesSalariaux.find(g => g.id == formData.groupe_salarial_id).taux_horaire).toLocaleString('fr-FR')} {symbole}/h
                      · {groupesSalariaux.find(g => g.id == formData.groupe_salarial_id).heures_journalieres}h/jour théoriques
                    </p>
                  )}
                </div>
              )}

              {/* Taux horaire personnalisé — visible si hourly */}
              {formData.salary_type === 'hourly' && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    Taux personnalisé{' '}
                    <span className="text-gray-400 font-normal text-xs">(optionnel - prioritaire sur le groupe)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={formData.taux_horaire_personnalise}
                      onChange={e => setFormData(p => ({ ...p, taux_horaire_personnalise: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none transition-all font-medium text-sm"
                      onFocus={(e) => { e.target.style.borderColor = '#448696'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; }}
                      placeholder="Laisser vide = taux du groupe"
                    />
                    <span className="text-xs font-bold text-gray-400 flex-shrink-0">{symbole}/h</span>
                  </div>
                  {formData.taux_horaire_personnalise && (
                    <p className="text-xs mt-1" style={{ color: '#448696' }}>
                      Taux appliqué : {parseFloat(formData.taux_horaire_personnalise).toLocaleString('fr-FR')} {symbole}/h
                    </p>
                  )}
                </div>
              )}

              {/* Mode de paiement */}
              <div className={formData.salary_type === 'hourly' ? 'sm:col-span-2' : ''}>
                <label className="text-sm font-semibold text-gray-700 block mb-2">
                  Mode de paiement du salaire
                </label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { value: 'especes',      label: 'Espèces' },
                    { value: 'virement',     label: 'Virement' },
                    { value: 'mobile_money', label: 'Mobile Money' }
                  ].map(({ value, label }) => (
                    <button key={value} type="button"
                      onClick={() => setFormData(p => ({ ...p, mode_paiement_salaire: value }))}
                      className="flex-1 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all"
                      style={{
                        backgroundColor: formData.mode_paiement_salaire === value ? '#448696' : 'white',
                        color: formData.mode_paiement_salaire === value ? 'white' : '#6b7280',
                        borderColor: formData.mode_paiement_salaire === value ? '#448696' : '#e5e7eb'
                      }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </div>


        </div>
      </div>
    </div>
  );

  // ============================================================================
  // 🔒 ÉTAPE 3 - SÉCURISÉE
  // ============================================================================
  const renderStep3 = () => (
    <div className="space-y-6">
      {renderError()}
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">

       <div className="px-6 py-4 flex items-center gap-3 rounded-t-2xl" style={{ backgroundColor: '#fed774' }}>
          <Upload size={24} className="text-gray-900" />
          <h3 className="font-bold text-gray-900 text-lg">Photo de profil (optionnel)</h3>
        </div>

        <div className="p-6">
          <div className="flex justify-center px-6 py-8 border-2 border-dashed rounded-xl" style={{ borderColor: '#e5e5e5' }}>
            {previewImage ? (
              <div className="relative">
                <img src={previewImage} alt="Preview" className="max-h-64 rounded-xl shadow-lg" />
                {/* 🔒 Bouton SÉCURISÉ */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPreviewImage(null);
                    setFormData(prev => ({ ...prev, photo: null }));
                  }}
                  className="absolute -top-3 -right-3 p-2 text-white rounded-full shadow-lg hover:shadow-xl transition-all"
                  style={{ backgroundColor: '#fed774' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fdd24e'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fed774'}
                >
                  <X size={20} className="text-gray-900" />
                </button>
              </div>
            ) : (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}>
                  <Upload className="text-gray-400" size={32} style={{ color: '#448696' }} />
                </div>
                {/* 🔒 Label SÉCURISÉ */}
                <label 
                  htmlFor="photo" 
                  className="cursor-pointer font-semibold text-base hover:opacity-80 transition-opacity inline-block" 
                  style={{ color: '#448696' }}
                  onClick={(e) => {
                    // Ne pas empêcher le comportement par défaut pour le label
                    e.stopPropagation();
                  }}
                >
                  Choisir une photo
                </label>
                {/* 🔒 Input SÉCURISÉ */}
                <input
                  id="photo"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleFileChange}
                  onClick={(e) => e.stopPropagation()}
                  className="hidden"
                />
                <p className="text-sm text-gray-500">JPG ou PNG (max 5MB)</p>
                <p className="text-xs text-gray-400">
                  Vous pouvez sauter cette étape
                </p>
              </div>
            )}
          </div>
          
          {/* 🔒 MESSAGE CLAIR */}
          <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: '#f0f9fb' }}>
            <p className="text-sm font-medium text-gray-700 text-center">
              Cliquez sur <span className="font-bold" style={{ color: '#448696' }}>"Créer l'employé"</span> pour finaliser l'enregistrement
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const stepLabels = ['Informations personnelles', 'Informations professionnelles', 'Photo'];

  return (
    <div className="space-y-6" style={{ backgroundColor: '#fdfcf5' }}>
     <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
      <div className="p-6 overflow-visible">
          {/* Indicateur d'étapes */}
          <div className="mb-8">
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 flex items-center justify-center font-bold text-base rounded-full transition-all duration-300 ${
                        currentStep >= step ? 'text-white shadow-lg' : 'text-gray-400 bg-gray-100'
                      }`}
                      style={{
                        backgroundColor: currentStep >= step ? '#448696' : undefined
                      }}
                    >
                      {currentStep > step ? <Check size={20} /> : step}
                    </div>
                    <span className="text-xs font-medium text-gray-600 mt-2 text-center max-w-[100px]">
                      {stepLabels[step - 1]}
                    </span>
                  </div>
                  {step < 3 && (
                    <div className="flex-1 h-1 mx-2 rounded-full transition-all duration-300" style={{ backgroundColor: currentStep > step ? '#448696' : '#e5e5e5' }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 🔒 FORM SÉCURISÉ */}
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            {/* 🔒 BOUTONS SÉCURISÉS */}
            <div className="mt-8 flex justify-end gap-3 border-t border-gray-100 pt-6">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToPreviousStep();
                  }}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200"
                >
                  Précédent
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    resetForm();
                  }}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200"
                >
                  Annuler
                </button>
              )}

              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToNextStep();
                  }}
                  className="px-6 py-3 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                  style={{ backgroundColor: '#448696' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6fa3b0'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#448696'}
                >
                  Suivant
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  onClick={(e) => {
                    console.log('🖱️ Clic sur bouton "Créer l\'employé"');
                  }}
                  className="px-6 py-3 text-gray-900 font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#fed774' }}
                  onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#fdd24e')}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fed774'}
                >
                  {loading ? 'Enregistrement...' : 'Créer l\'employé'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {toastInfo && (
        <Toast
          message={toastInfo.message}
          type={toastInfo.type}
          onClose={() => setToastInfo(null)}
        />
      )}

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default NewEmployeeFormSimple;